// Tasks Module
// Phase 2: UI shell + Phase 3: Tasks list.
// Renders the "Tasks" main menu with two tabs:
//   - Tasks:    filterable list of tasks (mirrors the Reports list)
//   - Planning: calendar planning tool (placeholder, no functionality yet)

import { apiCall } from '../core/api.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess, showConfirmDialog } from '../core/ui.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { getCurrentUser, getEffectiveAccountId } from '../utils/helpers.js';
import {
    loadReportTemplateWithElements,
    initTaskResolutionDocumentation,
    renderTaskResolutionDocumentation,
    getTaskResolutionDocumentation,
    createReportFromTaskResolution
} from './reports.js';

// ============================================================================
// Utilities
// ============================================================================

function escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
}

// Parse a YYYY-MM-DD input value into a local Date
function parseDateInput(val) {
    if (!val) return null;
    const parts = val.split('-');
    if (parts.length !== 3) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

// Format a UTC timestamp string for display (da-DK)
function formatTimestamp(ts) {
    if (!ts) return '<span class="text-gray-400 text-xs">-</span>';
    let t = ts;
    if (typeof t === 'string' && !t.endsWith('Z') && !t.includes('+')) {
        t += 'Z'; // mark as UTC
    }
    const d = new Date(t);
    if (isNaN(d.getTime())) return '<span class="text-gray-400 text-xs">-</span>';
    const date = d.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    return `<div>${date}</div><div class="text-xs text-gray-400">${time}</div>`;
}

// Module state
const tasksState = {
    currentTab: 'tasks' // 'tasks' | 'planning'
};

// Status display config
const STATUS_CONFIG = {
    new:         { label: 'New',         classes: 'bg-blue-100 text-blue-800 hover:bg-blue-200' },
    planned:     { label: 'Planned',     classes: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
    in_progress: { label: 'In Progress', classes: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' },
    done:        { label: 'Done',        classes: 'bg-green-100 text-green-800 hover:bg-green-200' },
    cancelled:   { label: 'Cancelled',   classes: 'bg-red-100 text-red-800 hover:bg-red-200' },
    archived:    { label: 'Archived',    classes: 'bg-gray-200 text-gray-600 hover:bg-gray-300' }
};

const PRIORITY_CONFIG = {
    low:    { label: 'Low',    classes: 'bg-gray-100 text-gray-600' },
    normal: { label: 'Normal', classes: 'bg-blue-50 text-blue-700' },
    high:   { label: 'High',   classes: 'bg-orange-100 text-orange-800' }
};

/**
 * Render the tab header (Tasks | Planning)
 */
function renderTabHeader() {
    const active = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
    const inactive = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
    return `
        <div class="flex items-center space-x-8">
            <button onclick="window.switchTasksTab('tasks')" id="tasks-tab-btn"
                    class="${tasksState.currentTab === 'tasks' ? active : inactive}">
                Tasks
            </button>
            <button onclick="window.switchTasksTab('planning')" id="planning-tab-btn"
                    class="${tasksState.currentTab === 'planning' ? active : inactive}">
                Planning
            </button>
        </div>
    `;
}

/**
 * Tasks tab content — filterable list of tasks
 */
function renderTasksTab() {
    return `
        <div class="px-4 sm:px-6 lg:px-8">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <div class="flex justify-end mb-3">
                    <button type="button" onclick="window.createTask()"
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Task
                    </button>
                </div>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-3">
                    <div>
                        <label for="task-date-filter" class="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <select id="task-date-filter" onchange="window.onTaskDateFilterChange()"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="all" selected>All dates</option>
                            <option value="month">Last month</option>
                            <option value="today">Today</option>
                            <option value="week">This week</option>
                            <option value="custom">Custom range</option>
                        </select>
                    </div>
                    <div id="task-custom-date-from-wrap" class="hidden">
                        <label for="task-date-from" class="block text-sm font-medium text-gray-700 mb-1">From</label>
                        <input type="date" id="task-date-from" onchange="window.filterTasks()"
                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                    </div>
                    <div id="task-custom-date-to-wrap" class="hidden">
                        <label for="task-date-to" class="block text-sm font-medium text-gray-700 mb-1">To</label>
                        <input type="date" id="task-date-to" onchange="window.filterTasks()"
                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                    </div>
                    <div>
                        <label for="task-location-filter" class="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <select id="task-location-filter" onchange="window.filterTasks()"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="all">All locations</option>
                        </select>
                    </div>
                    <div>
                        <label for="task-assignee-filter" class="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                        <select id="task-assignee-filter" onchange="window.filterTasks()"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="all">Anyone</option>
                        </select>
                    </div>
                    <div>
                        <label for="task-priority-filter" class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                        <select id="task-priority-filter" onchange="window.filterTasks()"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="all">Any priority</option>
                            <option value="high">High</option>
                            <option value="normal">Normal</option>
                            <option value="low">Low</option>
                        </select>
                    </div>
                    <div class="relative">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <button type="button" id="task-status-filter-btn" onclick="window.toggleTaskStatusDropdown()"
                                class="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm">
                            <span id="task-status-filter-display" class="block truncate">Active</span>
                            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </button>
                        <div id="task-status-dropdown" class="hidden absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg">
                            <div class="py-1" role="menu">
                                ${['new', 'planned', 'in_progress', 'done', 'cancelled', 'archived'].map(s => `
                                    <label class="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                                        <input type="checkbox" value="${s}" ${['new', 'planned', 'in_progress'].includes(s) ? 'checked' : ''}
                                               onchange="window.updateTaskStatusFilter()"
                                               class="task-status-checkbox h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500">
                                        <span class="ml-2 text-sm text-gray-700">${STATUS_CONFIG[s].label}</span>
                                    </label>
                                `).join('')}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-end">
                    <button type="button" onclick="window.clearTaskFilters()"
                            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        Clear filters
                    </button>
                </div>
            </div>

            <div class="mb-4 px-1 mt-6">
                <div class="text-sm text-gray-600">
                    <span id="tasks-total-count">0 tasks</span>
                </div>
            </div>

            <div class="mt-2 flow-root">
                <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-300">
                                <thead class="bg-gray-200">
                                    <tr>
                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Title</th>
                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Assigned To</th>
                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Scheduled</th>
                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Priority</th>
                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                                        <th scope="col" class="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">Actions</th>
                                    </tr>
                                </thead>
                                <tbody id="tasks-table-body" class="divide-y divide-gray-200 bg-white">
                                    <tr><td colspan="7" class="px-4 py-12 text-center text-sm text-gray-500">Loading tasks…</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Planning tab content — calendar (left 2/3) + unplanned tasks panel (right 1/3)
 */
function renderPlanningTab() {
    return `
        <div class="px-4 sm:px-6 lg:px-8">
            <div class="flex flex-col lg:flex-row gap-4">
                <!-- Calendar (left, 2/3) -->
                <div class="w-full lg:w-2/3 bg-white shadow rounded-lg p-4">
                    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div class="flex items-center gap-2">
                            <button onclick="window.planningNav('prev')" class="p-2 rounded-md hover:bg-gray-100 text-gray-600" title="Previous">
                                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <button onclick="window.planningNav('today')" class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Today</button>
                            <button onclick="window.planningNav('next')" class="p-2 rounded-md hover:bg-gray-100 text-gray-600" title="Next">
                                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                            </button>
                            <span id="planning-period-label" class="ml-2 text-base font-semibold text-gray-900"></span>
                        </div>
                        <div class="flex items-center gap-3">
                            <select id="planning-assignee" onchange="window.onPlanningAssigneeChange()"
                                    class="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-1.5">
                                <option value="all">All employees</option>
                            </select>
                            <div class="inline-flex rounded-md shadow-sm" role="group">
                                <button id="planning-view-day" onclick="window.setPlanningView('day')"
                                        class="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-l-md">Day</button>
                                <button id="planning-view-week" onclick="window.setPlanningView('week')"
                                        class="px-3 py-1.5 text-sm font-medium border border-gray-300 border-l-0 rounded-r-md">Week</button>
                            </div>
                        </div>
                    </div>
                    <div id="planning-calendar" class="overflow-auto" style="max-height: calc(100vh - 320px);"></div>
                </div>

                <!-- Unplanned tasks (right, 1/3) -->
                <div class="w-full lg:w-1/3 bg-white shadow rounded-lg p-4 flex flex-col">
                    <div class="flex items-center justify-between mb-3">
                        <h3 class="text-base font-semibold text-gray-900">Unplanned tasks</h3>
                        <span id="planning-unplanned-count" class="text-xs text-gray-500"></span>
                    </div>
                    <div class="mb-3">
                        <label for="planning-wish-filter" class="block text-xs font-medium text-gray-600 mb-1">Filter by wished date</label>
                        <select id="planning-wish-filter" onchange="window.onPlanningWishFilterChange()"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="period" selected>Wished for selected period</option>
                            <option value="all">All unplanned</option>
                            <option value="none">No wished date</option>
                        </select>
                    </div>
                    <div id="planning-unplanned-list" class="space-y-2 overflow-y-auto" style="max-height: calc(100vh - 360px);">
                        <p class="text-sm text-gray-500">Loading…</p>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Render the content area for the currently selected tab
 */
function renderTabContent() {
    const container = document.getElementById('tasks-tab-content');
    if (!container) return;
    if (tasksState.currentTab === 'planning') {
        container.innerHTML = renderPlanningTab();
        setTimeout(() => initPlanning(), 0);
        return;
    }
    container.innerHTML = renderTasksTab();
    // Populate filter dropdowns then load the list
    setTimeout(() => {
        populateTaskFilters();
        window.filterTasks();
    }, 0);
}

/**
 * Switch between the Tasks and Planning tabs
 * @param {string} tab - 'tasks' | 'planning'
 */
export function switchTasksTab(tab) {
    tasksState.currentTab = tab === 'planning' ? 'planning' : 'tasks';

    const active = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
    const inactive = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
    const tasksBtn = document.getElementById('tasks-tab-btn');
    const planningBtn = document.getElementById('planning-tab-btn');
    if (tasksBtn) tasksBtn.className = tasksState.currentTab === 'tasks' ? active : inactive;
    if (planningBtn) planningBtn.className = tasksState.currentTab === 'planning' ? active : inactive;

    renderTabContent();
}

// ============================================================================
// Filters population
// ============================================================================

async function populateTaskFilters() {
    const accountId = getEffectiveAccountId();
    if (!accountId) return;

    try {
        const [usersResp, locationsResp] = await Promise.all([
            apiCall(`/users/?account_id=${accountId}&include_child_accounts=true`, { headers: auth.getAuthHeaders() }),
            apiCall(`/locations?include_child_accounts=true`, { headers: auth.getAuthHeaders() })
        ]);

        const assigneeSelect = document.getElementById('task-assignee-filter');
        if (assigneeSelect) {
            const users = usersResp.users || [];
            assigneeSelect.innerHTML = '<option value="all">Anyone</option>' +
                users.map(u => `<option value="${u._id}">${escapeHtml(u.full_name || u.name || u.username || 'Unknown')}</option>`).join('');
        }

        const locationSelect = document.getElementById('task-location-filter');
        if (locationSelect) {
            const locations = locationsResp.locations || [];
            locationSelect.innerHTML = '<option value="all">All locations</option>' +
                locations.map(l => `<option value="${l.id}">${escapeHtml(l.name)}</option>`).join('');
        }
    } catch (err) {
        console.error('Failed to populate task filters:', err);
    }
}

// ============================================================================
// Status filter dropdown + filter controls
// ============================================================================

window.toggleTaskStatusDropdown = function () {
    const dd = document.getElementById('task-status-dropdown');
    if (dd) dd.classList.toggle('hidden');
};

window.updateTaskStatusFilter = function () {
    const checked = Array.from(document.querySelectorAll('.task-status-checkbox:checked')).map(cb => cb.value);
    const display = document.getElementById('task-status-filter-display');
    if (display) {
        if (checked.length === 0) display.textContent = 'None';
        else if (checked.length === 6) display.textContent = 'All';
        else display.textContent = checked.map(s => STATUS_CONFIG[s].label).join(', ');
    }
    window.filterTasks();
};

window.onTaskDateFilterChange = function () {
    const type = document.getElementById('task-date-filter')?.value;
    const fromWrap = document.getElementById('task-custom-date-from-wrap');
    const toWrap = document.getElementById('task-custom-date-to-wrap');
    const isCustom = type === 'custom';
    if (fromWrap) fromWrap.classList.toggle('hidden', !isCustom);
    if (toWrap) toWrap.classList.toggle('hidden', !isCustom);
    window.filterTasks();
};

window.clearTaskFilters = function () {
    const dateFilter = document.getElementById('task-date-filter');
    if (dateFilter) dateFilter.value = 'all';
    window.onTaskDateFilterChange();
    ['task-location-filter', 'task-assignee-filter', 'task-priority-filter'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = 'all';
    });
    document.querySelectorAll('.task-status-checkbox').forEach(cb => {
        cb.checked = ['new', 'planned', 'in_progress'].includes(cb.value);
    });
    window.updateTaskStatusFilter();
};

// ============================================================================
// Load + render the task list
// ============================================================================

window.filterTasks = async function () {
    const userData = getCurrentUser();
    const accountId = getEffectiveAccountId();
    const tableBody = document.getElementById('tasks-table-body');
    if (!tableBody) return;

    if (!accountId) {
        if (userData && userData.role === 'SuperAdmin') {
            tableBody.innerHTML = `<tr><td colspan="7" class="px-4 py-12 text-center text-sm text-gray-500">Please select an account to view tasks</td></tr>`;
        }
        return;
    }

    // Date range
    const dateType = document.getElementById('task-date-filter')?.value || 'all';
    let fromDate = null, toDate = null;
    const now = new Date();
    if (dateType === 'custom') {
        fromDate = parseDateInput(document.getElementById('task-date-from')?.value);
        toDate = parseDateInput(document.getElementById('task-date-to')?.value);
    } else if (dateType === 'month') {
        fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        toDate = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (dateType === 'today') {
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (dateType === 'week') {
        const day = now.getDay();
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + 6);
    }

    const locationId = document.getElementById('task-location-filter')?.value || 'all';
    const assigneeId = document.getElementById('task-assignee-filter')?.value || 'all';
    const priority = document.getElementById('task-priority-filter')?.value || 'all';
    const statuses = Array.from(document.querySelectorAll('.task-status-checkbox:checked')).map(cb => cb.value);

    // Build params
    let params = `?account_id=${accountId}`;
    if (fromDate && toDate) {
        params += `&from_date=${fromDate.toISOString().slice(0, 10)}&to_date=${toDate.toISOString().slice(0, 10)}`;
    }
    if (locationId !== 'all') params += `&location_id=${locationId}`;
    if (assigneeId !== 'all') params += `&assigned_user_id=${assigneeId}`;
    if (priority !== 'all') params += `&priority=${priority}`;
    if (statuses.length > 0) params += `&statuses=${statuses.join(',')}`;

    let tasks;
    try {
        tasks = await apiCall(`/tasks/${params}`, { headers: auth.getAuthHeaders() });
    } catch (err) {
        console.error('Failed to load tasks:', err);
        tableBody.innerHTML = `<tr><td colspan="7" class="px-4 py-12 text-center text-sm text-red-500">Failed to load tasks</td></tr>`;
        return;
    }

    const countEl = document.getElementById('tasks-total-count');

    if (!tasks || tasks.length === 0) {
        tableBody.innerHTML = `
            <tr><td colspan="7" class="px-4 py-12 text-center text-sm text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
                </svg>
                <h3 class="mt-2 text-sm font-semibold text-gray-900">No tasks found</h3>
                <p class="mt-1 text-sm text-gray-500">Get started by creating your first task.</p>
            </td></tr>`;
        if (countEl) countEl.textContent = '0 tasks';
        return;
    }

    tableBody.innerHTML = tasks.map(task => {
        const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.new;
        const priorityCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
        const locationName = task.location_name || 'N/A';
        const assignee = task.assigned_user_name
            ? escapeHtml(task.assigned_user_name)
            : '<span class="text-gray-400">Unassigned</span>';
        const scheduled = task.scheduled_start ? formatTimestamp(task.scheduled_start) : '<span class="text-gray-400 text-xs">-</span>';

        const statusBadge = `<span onclick="window.showTaskStatusMenu(event, '${task._id}', '${task.status}')"
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer transition-colors ${status.classes}">${status.label}</span>`;
        const priorityBadge = `<span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${priorityCfg.classes}">${priorityCfg.label}</span>`;

        const canResolve = !['done', 'cancelled', 'archived'].includes(task.status);
        const resolveBtn = canResolve
            ? `<button onclick="window.openResolveTask('${task._id}')" class="text-green-600 hover:text-green-800 mr-4">Resolve</button>`
            : '';

        return `
            <tr>
                <td class="py-4 pl-4 pr-3 text-sm font-medium sm:pl-6">
                    <span class="text-gray-900 cursor-pointer hover:text-primary-600 transition-colors" onclick="window.editTask('${task._id}')">${escapeHtml(task.title)}</span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${escapeHtml(locationName)}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${assignee}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${scheduled}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${priorityBadge}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${statusBadge}</td>
                <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    ${resolveBtn}<button onclick="window.deleteTask('${task._id}')" class="text-red-600 hover:text-red-900">Delete</button>
                </td>
            </tr>
        `;
    }).join('');

    if (countEl) countEl.textContent = `${tasks.length} task${tasks.length === 1 ? '' : 's'}`;
};

// ============================================================================
// Status change menu
// ============================================================================

window.showTaskStatusMenu = function (event, taskId, currentStatus) {
    event.stopPropagation();
    const existing = document.getElementById('task-status-change-menu');
    if (existing) existing.remove();

    const menu = document.createElement('div');
    menu.id = 'task-status-change-menu';
    menu.className = 'fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50';
    menu.style.left = (event.pageX + 10) + 'px';
    menu.style.top = event.pageY + 'px';
    menu.style.minWidth = '160px';

    menu.innerHTML = Object.keys(STATUS_CONFIG).map(s => {
        const disabled = s === currentStatus;
        return `
            <button onclick="window.changeTaskStatus('${taskId}', '${s}')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}"
                ${disabled ? 'disabled' : ''}>
                ${disabled ? '✓ ' : ''}${STATUS_CONFIG[s].label}
            </button>`;
    }).join('');

    document.body.appendChild(menu);

    const close = (e) => {
        if (!menu.contains(e.target)) {
            menu.remove();
            document.removeEventListener('click', close);
        }
    };
    setTimeout(() => document.addEventListener('click', close), 100);
};

window.changeTaskStatus = async function (taskId, newStatus) {
    const menu = document.getElementById('task-status-change-menu');
    if (menu) menu.remove();
    try {
        await apiCall(`/tasks/${taskId}`, {
            method: 'PATCH',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify({ status: newStatus })
        });
        showSuccess('Task status updated');
        await window.filterTasks();
    } catch (err) {
        console.error('Failed to change task status:', err);
        showError('Failed to update task status');
    }
};

// ============================================================================
// Create / Edit task modal (Phase 4)
// ============================================================================

// Holds transient state for the open modal
const taskModalState = {
    mode: 'create',     // 'create' | 'edit'
    taskId: null,
    images: [],         // array of TaskImage objects
    tagStructure: [],   // cached tag structure
    selectedTags: []    // tag ids currently selected
};

function closeTaskModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.innerHTML = '';
        modal.classList.add('hidden');
    }
}
window.closeTaskModal = closeTaskModal;

/**
 * Open the create/edit task modal.
 * @param {object|null} task - null to create, or an existing task to edit
 */
function openTaskModal(task) {
    const modal = document.getElementById('modal');
    if (!modal) return;

    taskModalState.mode = task ? 'edit' : 'create';
    taskModalState.taskId = task ? task._id : null;
    taskModalState.images = task && Array.isArray(task.images) ? [...task.images] : [];
    taskModalState.selectedTags = task && Array.isArray(task.tags) ? task.tags.map(String) : [];

    const t = task || {};
    const budget = t.budget || {};
    const scheduleWishDate = t.schedule_wish ? String(t.schedule_wish).slice(0, 10) : '';

    // Planned (exact) time — stored as UTC, edited in local time
    const plannedStart = parseUtc(t.scheduled_start);
    const pad2 = (n) => String(n).padStart(2, '0');
    const localDate = (d) => d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '';
    const localTime = (d) => d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
    const plannedDateVal = localDate(plannedStart);
    const plannedStartVal = localTime(plannedStart);

    const priorityOptions = ['low', 'normal', 'high']
        .map(p => `<option value="${p}" ${(t.priority || 'normal') === p ? 'selected' : ''}>${PRIORITY_CONFIG[p].label}</option>`)
        .join('');

    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-10 mx-auto p-6 border w-11/12 md:w-3/4 lg:w-2/3 xl:max-w-3xl shadow-lg rounded-md bg-white mb-10">
            <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-medium text-gray-900">${taskModalState.mode === 'edit' ? 'Edit Task' : 'Create Task'}</h3>
                <button onclick="window.closeTaskModal()" class="text-gray-400 hover:text-gray-600">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>

            <form id="task-form" class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700">Title <span class="text-red-500">*</span></label>
                    <input type="text" id="task-input-title" required value="${escapeHtml(t.title || '')}"
                           class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Location</label>
                        <select id="task-input-location" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="">Loading…</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Assigned To</label>
                        <select id="task-input-assignee" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            <option value="">Loading…</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Priority</label>
                        <select id="task-input-priority" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                            ${priorityOptions}
                        </select>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700">Description</label>
                    <textarea id="task-input-description" rows="4"
                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">${escapeHtml(t.description || '')}</textarea>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Images</label>
                    <div id="task-images-display" class="flex flex-wrap gap-3 mb-2"></div>
                    <input type="file" id="task-image-input" accept="image/*" multiple class="hidden" onchange="window.handleTaskImageUpload(event)">
                    <button type="button" onclick="document.getElementById('task-image-input').click()"
                            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                        Add Images
                    </button>
                    <span id="task-image-uploading" class="ml-2 text-sm text-gray-500 hidden">Uploading…</span>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <div id="task-tags-container" class="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50">
                        <p class="text-sm text-gray-500">Loading tags…</p>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700">Documentation Template</label>
                    <select id="task-input-template" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        <option value="">None</option>
                    </select>
                    <p class="text-xs text-gray-500 mt-1">Report template used when documenting the resolved task.</p>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Schedule Wish (date)</label>
                        <input type="date" id="task-input-schedule-wish" value="${scheduleWishDate}"
                               class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Schedule Wish (note)</label>
                        <input type="text" id="task-input-schedule-wish-note" value="${escapeHtml(t.schedule_wish_note || '')}"
                               placeholder="e.g. before summer"
                               class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                    </div>
                </div>

                <div class="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Planned time <span class="font-normal text-gray-500">(optional — for appointments at an exact time)</span></label>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label class="block text-xs text-gray-500">Date</label>
                            <input type="date" id="task-input-planned-date" value="${plannedDateVal}"
                                   class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        </div>
                        <div>
                            <label class="block text-xs text-gray-500">Start time</label>
                            <input type="time" id="task-input-planned-start" value="${plannedStartVal}"
                                   class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        </div>
                    </div>
                    <p class="text-xs text-gray-500 mt-2">Set a date to schedule this task on the planning calendar. Add a start time for an exact appointment — the end is derived from the budget time below.</p>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Budget: Time (minutes)</label>
                        <input type="number" min="0" id="task-input-budget-time" value="${budget.time_minutes ?? ''}"
                               class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Budget: Expense (DKK)</label>
                        <input type="number" min="0" step="0.01" id="task-input-budget-expense" value="${budget.expense_amount ?? ''}"
                               class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                    </div>
                </div>
            </form>

            <div class="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button onclick="window.closeTaskModal()" class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Cancel</button>
                <button id="task-save-btn" onclick="window.saveTask()" class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                    ${taskModalState.mode === 'edit' ? 'Save Changes' : 'Create Task'}
                </button>
            </div>
        </div>
    `;
    modal.classList.remove('hidden');

    renderTaskModalImages();
    populateTaskModalSelects(t);
    loadTaskModalTags();
}

/**
 * Populate location / assignee / template selects in the modal.
 */
async function populateTaskModalSelects(task) {
    const accountId = getEffectiveAccountId();
    if (!accountId) return;

    try {
        const [usersResp, locationsResp, templatesResp] = await Promise.all([
            apiCall(`/users/?account_id=${accountId}&include_child_accounts=true`, { headers: auth.getAuthHeaders() }),
            apiCall(`/locations?include_child_accounts=true`, { headers: auth.getAuthHeaders() }),
            apiCall(`/report-templates/templates?account_id=${accountId}`, { headers: auth.getAuthHeaders() }).catch(() => [])
        ]);

        const locationSelect = document.getElementById('task-input-location');
        if (locationSelect) {
            const locations = locationsResp.locations || [];
            locationSelect.innerHTML = '<option value="">No location</option>' +
                locations.map(l => `<option value="${l.id}" ${task.location_id === l.id ? 'selected' : ''}>${escapeHtml(l.name)}</option>`).join('');
        }

        const assigneeSelect = document.getElementById('task-input-assignee');
        if (assigneeSelect) {
            const users = usersResp.users || [];
            assigneeSelect.innerHTML = '<option value="">Unassigned</option>' +
                users.map(u => {
                    const name = u.full_name || u.name || u.username || 'Unknown';
                    return `<option value="${u._id}" ${task.assigned_user_id === u._id ? 'selected' : ''}>${escapeHtml(name)}</option>`;
                }).join('');
        }

        const templateSelect = document.getElementById('task-input-template');
        if (templateSelect) {
            const allTemplates = templatesResp.templates || templatesResp || [];
            const templates = allTemplates.filter(tpl => tpl.is_released === true);
            templateSelect.innerHTML = '<option value="">None</option>' +
                templates.map(tpl => `<option value="${tpl._id}" ${task.template_id === tpl._id ? 'selected' : ''}>${escapeHtml(tpl.name)}</option>`).join('');
        }
    } catch (err) {
        console.error('Failed to populate task modal selects:', err);
    }
}

/**
 * Load the account tag structure and render a grouped multi-select picker.
 */
async function loadTaskModalTags() {
    const container = document.getElementById('task-tags-container');
    if (!container) return;
    const accountId = getEffectiveAccountId();
    if (!accountId) {
        container.innerHTML = '<p class="text-sm text-gray-500">No tags available</p>';
        return;
    }

    try {
        const response = await apiCall(`/tags/structure?account_id=${accountId}`, { headers: auth.getAuthHeaders() });
        const structure = response.structure || [];
        taskModalState.tagStructure = structure;

        const categoriesHtml = structure.map(category => {
            const tags = category.children || [];
            if (tags.length === 0) return '';
            return `
                <div class="mb-3">
                    <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">${escapeHtml(category.name)}</div>
                    <div class="flex flex-wrap gap-2">
                        ${tags.map(tag => {
                            const color = tag.color || '#6B7280';
                            const checked = taskModalState.selectedTags.includes(String(tag.id));
                            return `
                                <label class="inline-flex items-center cursor-pointer">
                                    <input type="checkbox" value="${tag.id}" ${checked ? 'checked' : ''}
                                           onchange="window.toggleTaskTag('${tag.id}')"
                                           class="task-tag-checkbox sr-only peer">
                                    <span class="px-2 py-1 rounded text-xs font-medium border peer-checked:ring-2 peer-checked:ring-offset-1"
                                          style="background-color: ${color}20; color: ${color}; border-color: ${color}40;">
                                        ${escapeHtml(tag.name)}
                                    </span>
                                </label>
                            `;
                        }).join('')}
                    </div>
                </div>
            `;
        }).join('');

        container.innerHTML = categoriesHtml || '<p class="text-sm text-gray-500">No tags configured for this account</p>';
    } catch (err) {
        console.error('Failed to load tags:', err);
        container.innerHTML = '<p class="text-sm text-red-500">Error loading tags</p>';
    }
}

window.toggleTaskTag = function (tagId) {
    const id = String(tagId);
    const idx = taskModalState.selectedTags.indexOf(id);
    if (idx === -1) taskModalState.selectedTags.push(id);
    else taskModalState.selectedTags.splice(idx, 1);
};

/**
 * Upload selected images to the shared report image endpoint and store the result.
 */
window.handleTaskImageUpload = async function (event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    const uploadingEl = document.getElementById('task-image-uploading');
    if (uploadingEl) uploadingEl.classList.remove('hidden');

    for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const resp = await fetch('/api/reports/upload-image', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${auth.getToken()}` },
                body: formData
            });
            if (!resp.ok) throw new Error('Upload failed');
            const data = await resp.json();
            taskModalState.images.push({
                filename: data.filename,
                url: data.url,
                thumbnail: data.thumbnail,
                width: data.width,
                height: data.height
            });
            renderTaskModalImages();
        } catch (err) {
            console.error('Image upload failed:', err);
            showError('Failed to upload image');
        }
    }

    if (uploadingEl) uploadingEl.classList.add('hidden');
};

window.removeTaskImage = function (index) {
    taskModalState.images.splice(index, 1);
    renderTaskModalImages();
};

function renderTaskModalImages() {
    const display = document.getElementById('task-images-display');
    if (!display) return;
    if (taskModalState.images.length === 0) {
        display.innerHTML = '<p class="text-sm text-gray-400">No images added.</p>';
        return;
    }
    display.innerHTML = taskModalState.images.map((img, i) => `
        <div class="relative group">
            <img src="${escapeHtml(img.thumbnail || img.url)}" alt="${escapeHtml(img.filename || '')}"
                 class="h-20 w-20 object-cover rounded-md border border-gray-200">
            <button type="button" onclick="window.removeTaskImage(${i})"
                    class="absolute -top-2 -right-2 bg-red-600 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs shadow hover:bg-red-700">
                ×
            </button>
        </div>
    `).join('');
}

/**
 * Collect modal form values and POST (create) or PUT (edit) the task.
 */
window.saveTask = async function () {
    const accountId = getEffectiveAccountId();
    const title = document.getElementById('task-input-title')?.value.trim();
    if (!title) {
        showError('Title is required');
        return;
    }
    if (!accountId) {
        showError('No account selected');
        return;
    }

    const locationSelect = document.getElementById('task-input-location');
    const assigneeSelect = document.getElementById('task-input-assignee');
    const templateSelect = document.getElementById('task-input-template');

    const locationId = locationSelect?.value || null;
    const locationName = locationId ? locationSelect.options[locationSelect.selectedIndex].text : null;
    const assigneeId = assigneeSelect?.value || null;
    const assigneeName = assigneeId ? assigneeSelect.options[assigneeSelect.selectedIndex].text : null;
    const templateId = templateSelect?.value || null;

    const budgetTime = document.getElementById('task-input-budget-time')?.value;
    const budgetExpense = document.getElementById('task-input-budget-expense')?.value;
    let budget = null;
    if ((budgetTime && budgetTime !== '') || (budgetExpense && budgetExpense !== '')) {
        budget = {
            time_minutes: budgetTime ? parseInt(budgetTime, 10) : null,
            expense_amount: budgetExpense ? parseFloat(budgetExpense) : null,
            expense_currency: 'DKK'
        };
    }

    const scheduleWish = document.getElementById('task-input-schedule-wish')?.value || null;

    // Planned (exact) time — entered in local time, stored as UTC ISO.
    // End is derived from the budget time (minutes) when a start time is set.
    const plannedDate = document.getElementById('task-input-planned-date')?.value || null;
    const plannedStartTime = document.getElementById('task-input-planned-start')?.value || null;
    let scheduledStart = null;
    let scheduledEnd = null;
    if (plannedDate) {
        const startDt = new Date(`${plannedDate}T${plannedStartTime || '00:00'}`);
        if (!isNaN(startDt.getTime())) scheduledStart = startDt.toISOString();
        if (plannedStartTime && budget && budget.time_minutes) {
            const endDt = new Date(startDt.getTime() + budget.time_minutes * 60000);
            if (!isNaN(endDt.getTime())) scheduledEnd = endDt.toISOString();
        }
    }

    const payload = {
        title,
        description: document.getElementById('task-input-description')?.value.trim() || null,
        images: taskModalState.images,
        location_id: locationId,
        location_name: locationName,
        assigned_user_id: assigneeId,
        assigned_user_name: assigneeName,
        template_id: templateId,
        tags: taskModalState.selectedTags,
        schedule_wish: scheduleWish ? `${scheduleWish}T00:00:00` : null,
        schedule_wish_note: document.getElementById('task-input-schedule-wish-note')?.value.trim() || null,
        scheduled_start: scheduledStart,
        scheduled_end: scheduledEnd,
        budget,
        priority: document.getElementById('task-input-priority')?.value || 'normal'
    };

    const saveBtn = document.getElementById('task-save-btn');
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

    try {
        if (taskModalState.mode === 'edit' && taskModalState.taskId) {
            await apiCall(`/tasks/${taskModalState.taskId}`, {
                method: 'PUT',
                headers: auth.getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            showSuccess('Task updated');
        } else {
            await apiCall('/tasks/', {
                method: 'POST',
                headers: auth.getAuthHeaders(),
                body: JSON.stringify({ ...payload, account_id: accountId })
            });
            showSuccess('Task created');
        }
        closeTaskModal();
        await window.filterTasks();
    } catch (err) {
        console.error('Failed to save task:', err);
        showError('Failed to save task: ' + (err.message || 'Unknown error'));
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = taskModalState.mode === 'edit' ? 'Save Changes' : 'Create Task'; }
    }
};

// ============================================================================
// Row actions
// ============================================================================

window.createTask = function () {
    openTaskModal(null);
};

async function openExistingTask(taskId) {
    try {
        const task = await apiCall(`/tasks/${taskId}`, { headers: auth.getAuthHeaders() });
        openTaskModal(task);
    } catch (err) {
        console.error('Failed to load task:', err);
        showError('Failed to load task');
    }
}

window.viewTask = function (taskId) {
    openExistingTask(taskId);
};

window.editTask = function (taskId) {
    openExistingTask(taskId);
};

window.deleteTask = async function (taskId) {
    const confirmed = await showConfirmDialog({
        title: 'Delete task',
        message: 'Are you sure you want to delete this task? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        icon: 'warning'
    });
    if (!confirmed) return;
    try {
        await apiCall(`/tasks/${taskId}`, {
            method: 'DELETE',
            headers: auth.getAuthHeaders()
        });
        showSuccess('Task deleted');
        await window.filterTasks();
    } catch (err) {
        console.error('Failed to delete task:', err);
        showError('Failed to delete task');
    }
};

// ============================================================================
// Task resolution
// ============================================================================
// The resolve flow mirrors "Create Report": a 3-tab modal where
//   - Info:          read-only task context (the task description IS the info)
//                    plus the actual time spent and resolution notes
//   - Documentation: the linked report template's documentation collection,
//                    reusing the exact same UI/code as Create Report
//   - Expenses:      itemized actual expenses
// When a documentation template is linked, resolving creates a published
// Report from the collected entries and links it via resolution.report_id.

const resolutionModalState = {
    task: null,
    taskId: null,
    currentTab: 'info',  // 'info' | 'documentation' | 'expenses'
    template: null,      // loaded report template (when task.template_id is set)
    timeSpent: '',       // actual time spent (minutes), persisted across tab switches
    notes: '',           // resolution notes, persisted across tab switches
    expenses: []         // array of { description, amount, currency }
};

window.openResolveTask = async function (taskId) {
    try {
        const task = await apiCall(`/tasks/${taskId}`, { headers: auth.getAuthHeaders() });
        let template = null;
        if (task.template_id) {
            try {
                template = await loadReportTemplateWithElements(task.template_id);
            } catch (err) {
                console.error('Failed to load documentation template:', err);
                showError('Could not load the documentation template for this task');
            }
        }
        openResolveModal(task, template);
    } catch (err) {
        console.error('Failed to load task:', err);
        showError('Failed to load task');
    }
};

function closeResolveModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.innerHTML = '';
        modal.classList.add('hidden');
    }
}
window.closeResolveModal = closeResolveModal;

function resolveTabClass(rtab, active, first) {
    return `resolve-tab ${first ? '' : 'ml-8 '}whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm ${
        active
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;
}

function openResolveModal(task, template) {
    const modal = document.getElementById('modal');
    if (!modal) return;

    const existing = task.resolution || {};
    const budget = task.budget || {};

    resolutionModalState.task = task;
    resolutionModalState.taskId = task._id;
    resolutionModalState.currentTab = 'info';
    resolutionModalState.template = template || null;
    resolutionModalState.timeSpent = existing.time_spent_minutes != null
        ? String(existing.time_spent_minutes)
        : (budget.time_minutes != null ? String(budget.time_minutes) : '');
    resolutionModalState.notes = existing.notes || '';
    resolutionModalState.expenses = Array.isArray(existing.expenses)
        ? existing.expenses.map(e => ({ description: e.description || '', amount: e.amount ?? '', currency: e.currency || 'DKK', receipt_image: e.receipt_image || null }))
        : [];

    // Seed the shared documentation collection state (reused from Reports).
    if (template) {
        initTaskResolutionDocumentation(template, existing.documentation || []);
    }

    const hasDoc = !!template;

    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-10 mx-auto w-11/12 md:w-3/4 lg:w-2/3 xl:max-w-3xl shadow-xl rounded-lg bg-white mb-10">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h3 class="text-lg font-medium text-gray-900">Resolve Task</h3>
                    <p class="mt-1 text-sm text-gray-500">${escapeHtml(task.title)}</p>
                </div>
                <button onclick="window.closeResolveModal()" class="text-gray-400 hover:text-gray-600">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>

            <div class="border-b border-gray-200">
                <nav class="-mb-px flex px-6" aria-label="Tabs">
                    <button onclick="window.switchResolveTab('info')" data-rtab="info" class="${resolveTabClass('info', true, true)}">Info</button>
                    ${hasDoc ? `<button onclick="window.switchResolveTab('documentation')" data-rtab="documentation" class="${resolveTabClass('documentation', false, false)}">Documentation</button>` : ''}
                    <button onclick="window.switchResolveTab('expenses')" data-rtab="expenses" class="${resolveTabClass('expenses', false, false)}">Expenses</button>
                </nav>
            </div>

            <div class="px-6 py-6">
                <div id="resolution-tab-content" class="max-h-[28rem] overflow-y-auto"></div>
            </div>

            <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                <button onclick="window.closeResolveModal()" class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Cancel</button>
                <button id="resolution-progress-btn" onclick="window.saveResolution(false)" class="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">Save progress</button>
                <button id="resolution-save-btn" onclick="window.saveResolution(true)" class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">Mark as Resolved</button>
            </div>
        </div>
    `;
    modal.classList.remove('hidden');
    renderResolveTabContent();
}

window.switchResolveTab = function (tab) {
    resolutionModalState.currentTab = tab;
    document.querySelectorAll('.resolve-tab').forEach(btn => {
        const rtab = btn.dataset.rtab;
        btn.className = resolveTabClass(rtab, rtab === tab, rtab === 'info');
    });
    renderResolveTabContent();
};

function renderResolveTabContent() {
    const content = document.getElementById('resolution-tab-content');
    if (!content) return;
    if (resolutionModalState.currentTab === 'documentation') {
        renderResolveDocumentationTab(content);
    } else if (resolutionModalState.currentTab === 'expenses') {
        renderResolveExpensesTab(content);
    } else {
        renderResolveInfoTab(content);
    }
}

function renderResolveInfoTab(content) {
    const task = resolutionModalState.task;
    const budget = task.budget || {};
    const plannedTime = budget.time_minutes != null ? `${budget.time_minutes} min` : '—';
    const plannedExpense = budget.expense_amount != null ? `${budget.expense_amount} ${budget.expense_currency || 'DKK'}` : '—';
    const description = task.description
        ? escapeHtml(task.description)
        : '<span class="text-gray-400">No description</span>';

    content.innerHTML = `
        <div class="space-y-5">
            <div>
                <h4 class="text-sm font-medium text-gray-700 mb-1">Description</h4>
                <div class="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3">${description}</div>
            </div>

            <div class="grid grid-cols-2 gap-4">
                <div>
                    <span class="text-xs text-gray-500">Location</span>
                    <div class="text-sm text-gray-900">${escapeHtml(task.location_name || '—')}</div>
                </div>
                <div>
                    <span class="text-xs text-gray-500">Assigned to</span>
                    <div class="text-sm text-gray-900">${escapeHtml(task.assigned_user_name || '—')}</div>
                </div>
                <div>
                    <span class="text-xs text-gray-500">Planned time</span>
                    <div class="text-sm text-gray-900">${plannedTime}</div>
                </div>
                <div>
                    <span class="text-xs text-gray-500">Planned expense</span>
                    <div class="text-sm text-gray-900">${plannedExpense}</div>
                </div>
            </div>

            <div class="border-t border-gray-200 pt-4 space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700">Time spent (minutes)</label>
                    <input type="number" min="0" id="resolution-time-spent" value="${escapeHtml(resolutionModalState.timeSpent)}"
                           oninput="window.updateResolutionField('timeSpent', this.value)"
                           class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea id="resolution-notes" rows="3"
                              oninput="window.updateResolutionField('notes', this.value)"
                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">${escapeHtml(resolutionModalState.notes)}</textarea>
                </div>
            </div>
        </div>
    `;
}

window.updateResolutionField = function (field, value) {
    resolutionModalState[field] = value;
};

function renderResolveDocumentationTab(content) {
    content.innerHTML = '<div id="resolution-doc-host"></div>';
    const host = document.getElementById('resolution-doc-host');
    renderTaskResolutionDocumentation(host);
}

function renderResolveExpensesTab(content) {
    content.innerHTML = `
        <div class="space-y-4">
            <div class="flex items-center justify-between">
                <label class="block text-sm font-medium text-gray-700">Expenses</label>
                <button type="button" onclick="window.addResolutionExpense()" class="text-sm text-primary-600 hover:text-primary-800 font-medium">+ Add expense</button>
            </div>
            <div id="resolution-expenses"></div>
        </div>
    `;
    renderResolutionExpenses();
}

function resolutionExpenseTotal() {
    return resolutionModalState.expenses
        .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
        .toFixed(2);
}

function renderResolutionExpensesTotal() {
    const el = document.getElementById('resolution-expense-total');
    if (el) el.textContent = `${resolutionExpenseTotal()} DKK`;
}

function renderResolutionExpenses() {
    const container = document.getElementById('resolution-expenses');
    if (!container) return;
    if (resolutionModalState.expenses.length === 0) {
        container.innerHTML = '<p class="text-sm text-gray-400">No expenses added.</p>';
        return;
    }
    container.innerHTML = resolutionModalState.expenses.map((e, i) => `
        <div class="border border-gray-200 rounded-md p-3 mb-2">
            <div class="flex items-center gap-2">
                <input type="text" value="${escapeHtml(e.description || '')}" placeholder="Description"
                       oninput="window.updateResolutionExpense(${i}, 'description', this.value)"
                       class="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                <input type="number" min="0" step="0.01" value="${e.amount ?? ''}" placeholder="0.00"
                       oninput="window.updateResolutionExpense(${i}, 'amount', this.value)"
                       class="w-28 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                <span class="text-sm text-gray-500">DKK</span>
                <button type="button" onclick="window.removeResolutionExpense(${i})" class="text-red-500 hover:text-red-700" title="Remove">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>
            <div class="mt-2">
                ${renderResolutionReceipt(e, i)}
            </div>
        </div>
    `).join('') +
        `<div class="text-right text-sm text-gray-600 mt-1">Total: <span id="resolution-expense-total" class="font-medium">${resolutionExpenseTotal()} DKK</span></div>`;
}

function renderResolutionReceipt(expense, i) {
    if (expense._uploading) {
        return `<span class="inline-flex items-center text-sm text-gray-500">
                    <svg class="animate-spin h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                    Uploading receipt…
                </span>`;
    }
    if (expense.receipt_image) {
        const thumb = expense.receipt_image.thumbnail || expense.receipt_image.url || '';
        return `<div class="flex items-center gap-2">
                    <a href="${escapeHtml(expense.receipt_image.url || thumb)}" target="_blank" rel="noopener">
                        <img src="${escapeHtml(thumb)}" alt="Receipt" class="h-14 w-14 object-cover rounded border border-gray-200">
                    </a>
                    <span class="text-sm text-gray-600">Receipt attached</span>
                    <button type="button" onclick="window.removeResolutionReceipt(${i})" class="text-sm text-red-500 hover:text-red-700">Remove</button>
                </div>`;
    }
    return `<label class="inline-flex items-center text-sm text-primary-600 hover:text-primary-800 font-medium cursor-pointer">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                Attach receipt
                <input type="file" accept="image/*" class="hidden" onchange="window.handleResolutionReceiptUpload(${i}, event)">
            </label>`;
}

window.addResolutionExpense = function () {
    resolutionModalState.expenses.push({ description: '', amount: '', currency: 'DKK', receipt_image: null });
    renderResolutionExpenses();
};

window.removeResolutionExpense = function (index) {
    resolutionModalState.expenses.splice(index, 1);
    renderResolutionExpenses();
};

window.updateResolutionExpense = function (index, field, value) {
    if (!resolutionModalState.expenses[index]) return;
    resolutionModalState.expenses[index][field] = value;
    if (field === 'amount') renderResolutionExpensesTotal();
};

window.handleResolutionReceiptUpload = async function (index, event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    const expense = resolutionModalState.expenses[index];
    if (!file || !expense) return;

    expense._uploading = true;
    renderResolutionExpenses();

    try {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await fetch('/api/reports/upload-image', {
            method: 'POST',
            headers: { Authorization: `Bearer ${auth.getToken()}` },
            body: formData
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || `Upload failed (${resp.status})`);
        }
        const data = await resp.json();
        expense.receipt_image = {
            filename: data.filename,
            url: data.url,
            thumbnail: data.thumbnail || data.url,
            width: data.width || null,
            height: data.height || null
        };
    } catch (err) {
        console.error('Failed to upload receipt:', err);
        showError('Failed to upload receipt: ' + (err.message || 'Unknown error'));
    } finally {
        delete expense._uploading;
        renderResolutionExpenses();
    }
};

window.removeResolutionReceipt = function (index) {
    if (!resolutionModalState.expenses[index]) return;
    resolutionModalState.expenses[index].receipt_image = null;
    renderResolutionExpenses();
};

window.saveResolution = async function (finalize = true) {
    const taskId = resolutionModalState.taskId;
    if (!taskId) return;
    const task = resolutionModalState.task;
    const accountId = getEffectiveAccountId();

    const timeSpent = resolutionModalState.timeSpent;
    const notes = (resolutionModalState.notes || '').trim() || null;
    const expenses = resolutionModalState.expenses
        .filter(e => e.amount !== '' && e.amount != null && !isNaN(parseFloat(e.amount)))
        .map(e => ({
            description: (e.description || '').trim() || null,
            amount: parseFloat(e.amount),
            currency: 'DKK',
            receipt_image: e.receipt_image || null
        }));

    const btn = document.getElementById('resolution-save-btn');
    const progressBtn = document.getElementById('resolution-progress-btn');
    if (btn) btn.disabled = true;
    if (progressBtn) progressBtn.disabled = true;
    const activeBtn = finalize ? btn : progressBtn;
    if (activeBtn) activeBtn.textContent = 'Saving…';

    try {
        // Collect documentation and, only when finalizing, materialize it as a Report.
        let documentation = [];
        let reportId = (task.resolution && task.resolution.report_id) || null;
        if (resolutionModalState.template) {
            documentation = getTaskResolutionDocumentation().filter(Boolean);
            if (finalize && documentation.length > 0) {
                const location = task.location_id
                    ? { id: task.location_id, name: task.location_name || '' }
                    : null;
                const report = await createReportFromTaskResolution({
                    template: resolutionModalState.template,
                    title: task.title,
                    accountId,
                    location
                });
                reportId = (report && (report._id || report.id)) || reportId;
            }
        }

        const payload = {
            time_spent_minutes: timeSpent !== '' && timeSpent != null ? parseInt(timeSpent, 10) : null,
            expenses,
            expense_currency: 'DKK',
            notes,
            documentation,
            report_id: reportId
        };

        await apiCall(`/tasks/${taskId}/resolve?finalize=${finalize}`, {
            method: 'POST',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        showSuccess(finalize ? 'Task resolved' : 'Progress saved');
        closeResolveModal();
        await window.filterTasks();
    } catch (err) {
        console.error('Failed to save task resolution:', err);
        showError((finalize ? 'Failed to resolve task: ' : 'Failed to save progress: ') + (err.message || 'Unknown error'));
        if (btn) { btn.disabled = false; btn.textContent = 'Mark as Resolved'; }
        if (progressBtn) { progressBtn.disabled = false; progressBtn.textContent = 'Save progress'; }
    }
};

// ============================================================================
// Planning tab (calendar + unplanned panel)
// ============================================================================

const DAY_START_HOUR = 0;   // calendar grid covers the full 24 hours
const DAY_END_HOUR = 23;    // ...00:00 through 23:00
const DEFAULT_SCROLL_HOUR = 5; // default scroll position (05:00) on first render
const HOUR_HEIGHT = 48;     // px per hour
const PLANNING_SNAP_MINUTES = 60; // drag-and-drop snaps to the hour
const PLANNING_RESIZE_MINUTES = 30; // resize handle snaps to 30-min steps
const DEFAULT_PLANNED_MINUTES = 30; // default block length when no budget/end set

// Task currently being dragged onto the calendar (unplanned card or planned block)
let planningDragTask = null;

const planningState = {
    view: 'week',           // 'day' | 'week'
    anchor: new Date(),     // any date inside the visible period
    assigneeId: 'all',
    wishFilter: 'period',   // 'period' | 'all' | 'none'
    users: []
};

// --- date helpers -----------------------------------------------------------

function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Monday-based week start
function startOfWeek(d) {
    const date = startOfDay(d);
    const day = (date.getDay() + 6) % 7; // 0=Mon
    date.setDate(date.getDate() - day);
    return date;
}

function addDays(d, n) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

function toDateParam(d) {
    // local YYYY-MM-DD
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function getPlanningRange() {
    if (planningState.view === 'day') {
        const s = startOfDay(planningState.anchor);
        return { start: s, end: s, days: [s] };
    }
    const s = startOfWeek(planningState.anchor);
    const days = [];
    for (let i = 0; i < 7; i++) days.push(addDays(s, i));
    return { start: s, end: addDays(s, 6), days };
}

function parseUtc(ts) {
    if (!ts) return null;
    let t = ts;
    if (typeof t === 'string' && !t.endsWith('Z') && !t.includes('+')) t += 'Z';
    const d = new Date(t);
    return isNaN(d.getTime()) ? null : d;
}

// --- init / navigation ------------------------------------------------------

async function initPlanning() {
    // highlight current view button
    updatePlanningViewButtons();

    // populate assignee select
    const accountId = getEffectiveAccountId();
    if (accountId) {
        try {
            const usersResp = await apiCall(`/users/?account_id=${accountId}&include_child_accounts=true`, { headers: auth.getAuthHeaders() });
            planningState.users = usersResp.users || [];
            const sel = document.getElementById('planning-assignee');
            if (sel) {
                sel.innerHTML = '<option value="all">All employees</option>' +
                    planningState.users.map(u => `<option value="${u._id}" ${planningState.assigneeId === u._id ? 'selected' : ''}>${escapeHtml(u.full_name || u.name || u.username || 'Unknown')}</option>`).join('');
            }
        } catch (err) {
            console.error('Failed to load planning users:', err);
        }
    }

    refreshPlanning();
}

function updatePlanningViewButtons() {
    const active = 'px-3 py-1.5 text-sm font-medium border border-primary-500 bg-primary-600 text-white';
    const inactive = 'px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50';
    const dayBtn = document.getElementById('planning-view-day');
    const weekBtn = document.getElementById('planning-view-week');
    if (dayBtn) dayBtn.className = (planningState.view === 'day' ? active : inactive) + ' rounded-l-md';
    if (weekBtn) weekBtn.className = (planningState.view === 'week' ? active : inactive) + ' border-l-0 rounded-r-md';
}

window.setPlanningView = function (view) {
    planningState.view = view === 'day' ? 'day' : 'week';
    updatePlanningViewButtons();
    refreshPlanning();
};

window.planningNav = function (direction) {
    const step = planningState.view === 'day' ? 1 : 7;
    if (direction === 'today') planningState.anchor = new Date();
    else if (direction === 'prev') planningState.anchor = addDays(planningState.anchor, -step);
    else if (direction === 'next') planningState.anchor = addDays(planningState.anchor, step);
    refreshPlanning();
};

window.onPlanningAssigneeChange = function () {
    const sel = document.getElementById('planning-assignee');
    planningState.assigneeId = sel ? sel.value : 'all';
    refreshPlanning();
};

window.onPlanningWishFilterChange = function () {
    const sel = document.getElementById('planning-wish-filter');
    planningState.wishFilter = sel ? sel.value : 'period';
    loadUnplannedTasks();
};

function refreshPlanning() {
    updatePeriodLabel();
    renderCalendar();
    loadUnplannedTasks();
}

function updatePeriodLabel() {
    const label = document.getElementById('planning-period-label');
    if (!label) return;
    const { start, end } = getPlanningRange();
    if (planningState.view === 'day') {
        label.textContent = start.toLocaleDateString('da-DK', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } else {
        const opts = { day: '2-digit', month: 'short' };
        label.textContent = `${start.toLocaleDateString('da-DK', opts)} – ${end.toLocaleDateString('da-DK', { ...opts, year: 'numeric' })}`;
    }
}

// --- calendar rendering -----------------------------------------------------

async function renderCalendar() {
    const container = document.getElementById('planning-calendar');
    if (!container) return;
    const { start, end, days } = getPlanningRange();

    // build static grid first
    const hours = [];
    for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);
    const gridHeight = (DAY_END_HOUR - DAY_START_HOUR + 1) * HOUR_HEIGHT;

    const todayStr = toDateParam(new Date());
    const dayHeaders = days.map(d => {
        const isToday = toDateParam(d) === todayStr;
        return `<div class="flex-1 text-center py-2 text-xs font-semibold ${isToday ? 'text-primary-600' : 'text-gray-600'} border-l border-gray-200">
            ${d.toLocaleDateString('da-DK', { weekday: 'short' })}<br>
            <span class="${isToday ? 'inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-600 text-white' : ''}">${d.getDate()}</span>
        </div>`;
    }).join('');

    const hourLabels = hours.map((h, i) => `
        <div style="top:${i * HOUR_HEIGHT}px" class="absolute right-0 pr-2 -translate-y-1/2 text-xs text-gray-400">${String(h).padStart(2, '0')}:00</div>
    `).join('');

    const dayColumns = days.map((d, idx) => `
        <div class="flex-1 relative border-l border-gray-200" data-day-index="${idx}" data-day="${toDateParam(d)}" style="height:${gridHeight}px">
            ${hours.map((h, i) => `<div style="top:${i * HOUR_HEIGHT}px;height:${HOUR_HEIGHT}px" class="absolute left-0 right-0 border-t border-gray-100"></div>`).join('')}
        </div>
    `).join('');

    container.innerHTML = `
        <div class="min-w-[640px]">
            <div class="flex sticky top-0 bg-white z-10 border-b border-gray-200">
                <div class="w-14 flex-shrink-0"></div>
                ${dayHeaders}
            </div>
            <div class="flex">
                <div class="w-14 flex-shrink-0 relative" style="height:${gridHeight}px">${hourLabels}</div>
                <div class="flex flex-1 relative">${dayColumns}</div>
            </div>
        </div>
    `;

    // default scroll position so the early-morning hours are hidden until needed
    if (!container.dataset.scrolled) {
        container.scrollTop = DEFAULT_SCROLL_HOUR * HOUR_HEIGHT;
        container.dataset.scrolled = '1';
    }

    attachCalendarDropZones();

    // fetch + place tasks
    const accountId = getEffectiveAccountId();
    if (!accountId) return;

    let params = `?account_id=${accountId}&scheduled_from=${toDateParam(start)}&scheduled_to=${toDateParam(end)}&statuses=new,planned,in_progress,done`;
    if (planningState.assigneeId !== 'all') params += `&assigned_user_id=${planningState.assigneeId}`;

    let tasks;
    try {
        tasks = await apiCall(`/tasks/${params}`, { headers: auth.getAuthHeaders() });
    } catch (err) {
        console.error('Failed to load planned tasks:', err);
        return;
    }

    placePlannedTasks(tasks || [], days);
}

function placePlannedTasks(tasks, days) {
    const dayIndexByDate = {};
    days.forEach((d, i) => { dayIndexByDate[toDateParam(d)] = i; });
    const columns = document.querySelectorAll('#planning-calendar [data-day-index]');

    tasks.forEach(task => {
        const start = parseUtc(task.scheduled_start);
        if (!start) return;
        const dateKey = toDateParam(start);
        const colIndex = dayIndexByDate[dateKey];
        if (colIndex === undefined) return;
        const col = columns[colIndex];
        if (!col) return;

        const end = parseUtc(task.scheduled_end);
        const startHours = start.getHours() + start.getMinutes() / 60;
        const budgetMinutes = task.budget && task.budget.time_minutes ? task.budget.time_minutes : null;
        let durationHours;
        if (end && end > start) durationHours = (end - start) / 3600000;
        else if (budgetMinutes) durationHours = budgetMinutes / 60;
        else durationHours = DEFAULT_PLANNED_MINUTES / 60;

        const top = Math.max(0, (startHours - DAY_START_HOUR) * HOUR_HEIGHT);
        const height = Math.max(PLANNING_RESIZE_MINUTES / 60 * HOUR_HEIGHT, durationHours * HOUR_HEIGHT) - 2;

        const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.new;
        const timeStr = start.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });

        const block = document.createElement('div');
        block.className = `absolute left-1 right-1 rounded px-1.5 py-1 text-xs cursor-pointer shadow-sm overflow-hidden ${status.classes}`;
        block.style.top = `${top}px`;
        block.style.height = `${height}px`;
        block.title = task.title;
        block.draggable = true;
        block.addEventListener('dragstart', (e) => {
            planningDragTask = task;
            block.classList.add('opacity-50');
            if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        });
        block.addEventListener('dragend', () => {
            planningDragTask = null;
            block.classList.remove('opacity-50');
            clearDropPreview();
        });
        block.onclick = () => { if (!block.dataset.resized) window.viewTask(task._id); };
        block.innerHTML = `<div class="font-medium truncate">${escapeHtml(task.title)}</div>
            <div class="truncate opacity-75">${timeStr}${task.assigned_user_name ? ' · ' + escapeHtml(task.assigned_user_name) : ''}</div>`;

        // bottom resize handle — drag to change budget time in 30-min steps
        const handle = document.createElement('div');
        handle.className = 'absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize';
        handle.addEventListener('mousedown', (e) => startBlockResize(e, block, task, start));
        block.appendChild(handle);

        col.appendChild(block);
    });
}

// --- drag-and-drop planning -------------------------------------------------

function startBlockResize(e, block, task, start) {
    e.preventDefault();
    e.stopPropagation();
    block.draggable = false; // suspend HTML5 drag while resizing

    const stepPx = (PLANNING_RESIZE_MINUTES / 60) * HOUR_HEIGHT;
    const startY = e.clientY;
    const startHeight = block.offsetHeight;
    let minutes = Math.round((startHeight + 2) / HOUR_HEIGHT * 60 / PLANNING_RESIZE_MINUTES) * PLANNING_RESIZE_MINUTES;

    const onMove = (ev) => {
        const delta = ev.clientY - startY;
        const steps = Math.round(delta / stepPx);
        minutes = Math.max(
            PLANNING_RESIZE_MINUTES,
            Math.round((startHeight + 2) / HOUR_HEIGHT * 60 / PLANNING_RESIZE_MINUTES) * PLANNING_RESIZE_MINUTES
                + steps * PLANNING_RESIZE_MINUTES
        );
        block.style.height = `${(minutes / 60) * HOUR_HEIGHT - 2}px`;
        block.dataset.resized = '1';
    };

    const onUp = async () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        block.draggable = true;
        if (!block.dataset.resized) return;

        const endDt = new Date(start.getTime() + minutes * 60000);
        const newBudget = Object.assign({}, task.budget || {}, {
            time_minutes: minutes,
            expense_currency: (task.budget && task.budget.expense_currency) || 'DKK'
        });
        try {
            await apiCall(`/tasks/${task._id}`, {
                method: 'PATCH',
                headers: auth.getAuthHeaders(),
                body: JSON.stringify({ scheduled_end: endDt.toISOString(), budget: newBudget })
            });
            refreshPlanning();
        } catch (err) {
            console.error('Failed to update task duration:', err);
            showError('Failed to update task duration');
        }
        // allow the click handler to fire normally on the next render
        setTimeout(() => { delete block.dataset.resized; }, 0);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
}

function attachCalendarDropZones() {
    const columns = document.querySelectorAll('#planning-calendar [data-day-index]');
    columns.forEach(col => {
        col.addEventListener('dragover', (e) => {
            if (!planningDragTask) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
            col.classList.add('bg-primary-50');
            showDropPreview(col, e.clientY);
        });
        col.addEventListener('dragleave', (e) => {
            // ignore leaves that bubble from child elements still inside the column
            if (e.relatedTarget && col.contains(e.relatedTarget)) return;
            col.classList.remove('bg-primary-50');
            clearDropPreview();
        });
        col.addEventListener('drop', (e) => {
            col.classList.remove('bg-primary-50');
            clearDropPreview();
            handleCalendarDrop(col, e);
        });
    });
}

function snapHourFromY(col, clientY) {
    const rect = col.getBoundingClientRect();
    const offsetY = clientY - rect.top;
    let hourFloat = DAY_START_HOUR + offsetY / HOUR_HEIGHT;
    const snap = PLANNING_SNAP_MINUTES / 60;
    hourFloat = Math.round(hourFloat / snap) * snap;
    hourFloat = Math.max(0, Math.min(24 - snap, hourFloat));
    const hours = Math.floor(hourFloat);
    const minutes = Math.round((hourFloat - hours) * 60);
    return { hours, minutes };
}

let dropPreviewEl = null;

function plannedDurationMinutes(task) {
    if (task && task.budget && task.budget.time_minutes) return task.budget.time_minutes;
    return DEFAULT_PLANNED_MINUTES;
}

function showDropPreview(col, clientY) {
    if (!planningDragTask) return;
    const { hours, minutes } = snapHourFromY(col, clientY);
    const startHours = hours + minutes / 60;
    const durationHours = plannedDurationMinutes(planningDragTask) / 60;
    const top = Math.max(0, (startHours - DAY_START_HOUR) * HOUR_HEIGHT);
    const height = Math.max(PLANNING_RESIZE_MINUTES / 60 * HOUR_HEIGHT, durationHours * HOUR_HEIGHT) - 2;
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    if (!dropPreviewEl) {
        dropPreviewEl = document.createElement('div');
        dropPreviewEl.className = 'absolute left-1 right-1 rounded px-1.5 py-1 text-xs pointer-events-none '
            + 'bg-primary-200/60 border-2 border-dashed border-primary-500 text-primary-800 z-20';
    }
    dropPreviewEl.style.top = `${top}px`;
    dropPreviewEl.style.height = `${height}px`;
    dropPreviewEl.innerHTML = `<div class="font-medium truncate">${escapeHtml(planningDragTask.title)}</div>
        <div class="truncate opacity-80">${timeStr}</div>`;
    if (dropPreviewEl.parentElement !== col) col.appendChild(dropPreviewEl);
}

function clearDropPreview() {
    if (dropPreviewEl && dropPreviewEl.parentElement) {
        dropPreviewEl.parentElement.removeChild(dropPreviewEl);
    }
}

async function handleCalendarDrop(col, e) {
    e.preventDefault();
    const task = planningDragTask;
    planningDragTask = null;
    if (!task) return;

    const dateStr = col.dataset.day; // YYYY-MM-DD (local)
    if (!dateStr) return;
    const { hours, minutes } = snapHourFromY(col, e.clientY);
    const [y, m, d] = dateStr.split('-').map(Number);
    const startDt = new Date(y, m - 1, d, hours, minutes, 0, 0);

    const updates = { scheduled_start: startDt.toISOString() };

    // Derive the end from the budgeted time when available
    const minutesBudget = task.budget && task.budget.time_minutes;
    if (minutesBudget) {
        updates.scheduled_end = new Date(startDt.getTime() + minutesBudget * 60000).toISOString();
    } else {
        updates.scheduled_end = null;
    }

    // Assign to the selected employee only when a specific one is in focus;
    // in the "all" view we keep whatever assignment already exists.
    if (planningState.assigneeId !== 'all') {
        updates.assigned_user_id = planningState.assigneeId;
        const u = planningState.users.find(x => x._id === planningState.assigneeId);
        updates.assigned_user_name = u ? (u.full_name || u.name || u.username || null) : null;
    }

    try {
        await apiCall(`/tasks/${task._id}`, {
            method: 'PATCH',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify(updates)
        });
        refreshPlanning();
    } catch (err) {
        console.error('Failed to schedule task:', err);
        showError('Failed to schedule task');
    }
}

// --- unplanned panel --------------------------------------------------------

async function loadUnplannedTasks() {
    const list = document.getElementById('planning-unplanned-list');
    const countEl = document.getElementById('planning-unplanned-count');
    if (!list) return;
    const accountId = getEffectiveAccountId();
    if (!accountId) return;

    let params = `?account_id=${accountId}&scheduled=false&statuses=new,planned,in_progress`;
    if (planningState.wishFilter === 'none') {
        params += `&has_wish=false`;
    } else if (planningState.wishFilter === 'period') {
        const { start, end } = getPlanningRange();
        params += `&wish_from=${toDateParam(start)}&wish_to=${toDateParam(end)}`;
    }

    let tasks;
    try {
        tasks = await apiCall(`/tasks/${params}`, { headers: auth.getAuthHeaders() });
    } catch (err) {
        console.error('Failed to load unplanned tasks:', err);
        list.innerHTML = '<p class="text-sm text-red-500">Failed to load tasks</p>';
        return;
    }

    if (countEl) countEl.textContent = `${(tasks || []).length}`;

    if (!tasks || tasks.length === 0) {
        list.innerHTML = '<p class="text-sm text-gray-500 py-4 text-center">No unplanned tasks</p>';
        return;
    }

    list.innerHTML = tasks.map(task => {
        const priorityCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
        const wish = parseUtc(task.schedule_wish);
        let wishLine = '';
        if (wish) {
            wishLine = `<span class="inline-flex items-center text-primary-700"><svg class="h-3 w-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>${wish.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit' })}</span>`;
        } else if (task.schedule_wish_note) {
            wishLine = `<span class="italic text-gray-500">“${escapeHtml(task.schedule_wish_note)}”</span>`;
        } else {
            wishLine = `<span class="text-gray-400">No wished date</span>`;
        }
        return `
            <div data-unplanned-id="${task._id}" draggable="true"
                 onclick="window.editTask('${task._id}')"
                 class="border border-gray-200 rounded-md p-2.5 hover:border-primary-400 hover:shadow cursor-grab active:cursor-grabbing bg-white">
                <div class="flex items-start justify-between gap-2">
                    <div class="text-sm font-medium text-gray-900 truncate">${escapeHtml(task.title)}</div>
                    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 ${priorityCfg.classes}">${priorityCfg.label}</span>
                </div>
                ${task.location_name ? `<div class="text-xs text-gray-500 mt-0.5 truncate">${escapeHtml(task.location_name)}</div>` : ''}
                <div class="text-xs mt-1">${wishLine}</div>
            </div>
        `;
    }).join('');

    // wire up drag sources (store full task objects by id)
    const taskById = {};
    tasks.forEach(t => { taskById[t._id] = t; });
    list.querySelectorAll('[data-unplanned-id]').forEach(card => {
        card.addEventListener('dragstart', (e) => {
            planningDragTask = taskById[card.dataset.unplannedId] || null;
            card.classList.add('opacity-50');
            if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        });
        card.addEventListener('dragend', () => {
            planningDragTask = null;
            card.classList.remove('opacity-50');
            clearDropPreview();
        });
    });
}

/**
 * Show the Tasks page
 */
export function showTasks() {
    updateActiveNavigation('tasks');

    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view tasks');
        return;
    }

    // Render tabs into the header page title (same pattern as Report Templates)
    const pageTitleElement = document.getElementById('page-title');
    if (pageTitleElement) {
        pageTitleElement.innerHTML = renderTabHeader();
    } else {
        updatePageTitle('Tasks');
    }

    mainContent.innerHTML = `<div id="tasks-tab-content" class="mt-6"></div>`;
    renderTabContent();
}
